/**
 * SpectrumScreen (mobile RN) — all 15 DMT lines + Olympian / Mental Order.
 * A tool with no intake shows a "take intake" prompt; tapping a line opens its
 * detail (Mala-D + certification challenge + "I passed" button). Mirrors web.
 *
 * New-system model: ORDER holds 5 gears × 3 ascension tiers (DEMI anchor +
 * TYTN + OLMPN, from ascension.ts). STAGE is no longer a ring system here
 * — it's ORDER's chronological layer, rendered elsewhere. SHAPE is hidden
 * until unlocked (ASTRA acquired, or a manual toggle). SWAP (the malady↔deity
 * trade table) is off by default, per gear.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Image, ImageSourcePropType, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { crossAlert } from '../lib/crossAlert';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { GEARS, GEAR_META } from '../domain/olmps';
import { hasIntake, loadState, markCertified } from './spectrum-data';
import { olympianScore, mentalOrder, SpectrumState } from './spectrum';
import { DMT_TOOLS, getTool, normalizeMaladyId, DmtTool, DmtToolId } from './spectrum-tools';
import { GEAR_TIERS, TIER_ORDER, TIER_LABEL, GearId, TierId, ascensionRank, tierOf } from './ascension';
import { useCompassConfig, CustomSwap } from './sectorConfig';
import { SpectrumLine } from './SpectrumLine';
import { SpectrumIntake } from './SpectrumIntake';
import { getSwapEntry, SwapEntry } from './swap-data';
import { getDeity, acquisitionTiers, TIER_LABELS, deityLevelData, principlesForLevel } from './deity-data';
import { computeProgress, todayKey, isPickPrompt, type ProgressStats } from './deity-progress';
import { ACQUIRE_POINTS, POINTS_UNIT, type GameSettings } from './gameSettings';
import { dmtArt, maladyArt } from './deityArt';
import { Atmos, GlassCard, Medallion } from '../v10/ui';
import { portraitFor } from '../v10/roster';

interface Row {
  toolId: DmtToolId;
  toolName: string;
  maladyName: string;
  maladyId: string;
  state: SpectrumState;
  certified: boolean;
  intakeTaken: boolean;
}

type PageId = 'order' | 'shape';

/** The card the user has zoomed into to flip — a deity (with its gear/tier) or
 *  a malady (row carries the specific malady subtype being viewed). */
type FocusTarget =
  | { kind: 'deity'; row: Row; tier: TierId; gear: GearId }
  | { kind: 'malady'; row: Row };

const PAGE_NOTE: Record<PageId, string> = {
  order: '5 process prongs · 3 ascension tiers each (DEMI → TYTN → OLMPN)',
  shape: 'Life sectors · pick a deity · roster still to lock',
};

const SHAPE_SECTORS = ['ECONOMIC', 'SOCIAL', 'HEALTH', 'ADMIN', 'PERSONAL'];

export function SpectrumScreen({ userId }: { userId: string | undefined }) {
  const { t } = useTheme();
  const styles = useStyles();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [intakeTool, setIntakeTool] = useState<DmtTool | null>(null);
  const [detail, setDetail] = useState<Row | null>(null);
  const [focus, setFocus] = useState<FocusTarget | null>(null);
  const [page, setPage] = useState<PageId>('order');

  const {
    config: compassConfig,
    setShapeEnabled,
    togglePrinciple,
    logPrincipleToday,
    addSwap,
    updateSwap,
    removeSwap,
  } = useCompassConfig(userId);
  const todayStr = todayKey();
  const progressFor = useCallback(
    (toolId: DmtToolId) => computeProgress(compassConfig.principleProgress[toolId], deityLevelData(toolId), todayStr, toolId, {
        ledger: compassConfig.ledger,
        game: compassConfig.game,
      }),
    [compassConfig.principleProgress, compassConfig.ledger, compassConfig.game, todayStr]
  );

  // If SHAPE gets locked again (or was never unlocked) while it's the active
  // tab, fall back to ORDER so the screen never strands the user on a hidden tab.
  useEffect(() => {
    if (page === 'shape' && !compassConfig.shapeEnabled) setPage('order');
  }, [page, compassConfig.shapeEnabled]);

  // ASTRA-secured auto-unlock: once the user has secured ASTRA (Orient
  // OLMPN), SHAPE unlocks automatically — no manual toggle required. Fires
  // once per mount (guarded by the ref below) so it never fights a user who
  // manually re-locks SHAPE afterward.
  const shapeAutoUnlocked = useRef(false);
  useEffect(() => {
    if (shapeAutoUnlocked.current) return;
    if (progressFor('astra').secured && !compassConfig.shapeEnabled) {
      shapeAutoUnlocked.current = true;
      setShapeEnabled(true);
    }
  }, [progressFor, compassConfig.shapeEnabled, setShapeEnabled]);

  const load = useCallback(async () => {
    if (!userId) return;
    const loaded = await Promise.all(
      DMT_TOOLS.map(async (tool) => {
        const [{ state, maladySubtype, certified }, intakeTaken] = await Promise.all([
          loadState(supabase, userId, tool.id),
          hasIntake(supabase, userId, tool.id),
        ]);
        const maladyRow = tool.maladies.find((mm) => mm.id === maladySubtype) ?? tool.maladies[0];
        return {
          toolId: tool.id,
          toolName: tool.name,
          maladyName: maladyRow?.name ?? '',
          maladyId: maladyRow?.id ?? '',
          state,
          certified,
          intakeTaken,
        };
      }),
    );
    setRows(loaded);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const requestUnlockShape = () => {
    crossAlert('Unlock the SHAPE ring?', 'SHAPE (your life sectors) normally unlocks once you acquire ASTRA. You can also switch it on early.', [
      { text: 'Not yet', style: 'cancel' },
      { text: 'Unlock', onPress: () => setShapeEnabled(true) },
    ]);
  };

  if (intakeTool) {
    return (
      <SpectrumIntake
        tool={intakeTool}
        userId={userId}
        onDone={() => {
          setIntakeTool(null);
          load();
        }}
      />
    );
  }

  if (detail) {
    const tool = getTool(detail.toolId);
    const stats = progressFor(detail.toolId);
    const secured = stats.secured;
    const entry = getSwapEntry(detail.toolId, detail.maladyId);
    const pos = detail.state.displayed;
    return (
      <Atmos>
      <ScrollView contentContainerStyle={styles.detail}>
        <Pressable onPress={() => setDetail(null)}>
          <Text style={styles.back}>← back</Text>
        </Pressable>

        <Placard
          name={detail.toolName}
          subtitle={entry?.ability}
          image={dmtArt(detail.toolId)}
          variant="deity"
        />

        <SpectrumLine
          toolName={detail.toolName}
          maladyName={detail.maladyName}
          displayed={detail.state.displayed}
          repCount={stats.steps}
          acquisition={secured ? 'secured' : STAGE_LABELS[stats.tierIdx].toLowerCase()}
          secured={secured}
        />

        <View style={styles.statsRow}>
          <StatTile value={`Lvl ${stats.level}`} label="LEVEL" />
          <StatTile value={pos > 0 ? `+${pos}` : `${pos}`} label="POSITION" />
          <StatTile value={`${stats.steps}`} label="STEPS" />
          <StatTile value={secured ? 'SECURED' : STAGE_LABELS[stats.tierIdx]} label="STATUS" small />
        </View>

        <Placard
          name={detail.maladyName}
          subtitle="the pattern this slot fights"
          image={maladyArt(detail.maladyId)}
          variant="malady"
        />

        <SwapSection row={detail} />

        <GlassCard style={styles.card}>
          <Text style={styles.cardLabel}>CERTIFICATION CHALLENGE</Text>
          <Text style={styles.cardBody}>{tool.certification}</Text>
          {detail.certified ? (
            <Text style={styles.certified}>✓ Certified</Text>
          ) : (
            <Pressable
              onPress={async () => {
                if (!userId) return;
                await markCertified(supabase, userId, detail.toolId);
                setDetail(null);
                load();
              }}
              style={styles.certButton}
            >
              <Text style={styles.certButtonText}>I passed this challenge</Text>
            </Pressable>
          )}
          <Text style={styles.fine}>
            One of four gates — also +3 held four weeks, enough reps, and reps under friction — before it secures.
          </Text>
        </GlassCard>
      </ScrollView>
      </Atmos>
    );
  }

  const states = rows?.map((r) => r.state) ?? [];
  const securedCount = DMT_TOOLS.filter((t) => progressFor(t.id as DmtToolId).secured).length;
  const rank = ascensionRank(securedCount);
  const rankSubtitle =
    securedCount < 5
      ? `DEMI ${securedCount}/5`
      : securedCount < 10
      ? `TYTN ${securedCount}/10`
      : `OLMPN ${securedCount}/15`;

  const rowFor = (toolId: DmtToolId): Row | undefined => rows?.find((r) => r.toolId === toolId);

  const tabs: PageId[] = compassConfig.shapeEnabled ? ['order', 'shape'] : ['order'];

  return (
    <Atmos>
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.scoreRow}>
        <ScoreCard label="OLMPN" value={rank} hint={rankSubtitle} />
        <ScoreCard label="Mental Order" value={`${mentalOrder(states)}`} hint="this week" />
      </View>

      <View style={styles.tabs}>
        {tabs.map((p) => (
          <Pressable
            key={p}
            onPress={() => setPage(p)}
            style={[styles.tab, page === p && styles.tabOn]}
          >
            <Text style={[styles.tabText, page === p && styles.tabTextOn]}>{p.toUpperCase()}</Text>
          </Pressable>
        ))}
      </View>
      <Text style={styles.tabNote}>{PAGE_NOTE[page]}</Text>

      {rows === null ? (
        <Text style={styles.loading}>Reading your spectrum…</Text>
      ) : page === 'shape' ? (
        SHAPE_SECTORS.map((sec) => (
          <GlassCard key={sec} style={styles.slot}>
            <Text style={styles.seclabel}>{sec}</Text>
            <Text style={styles.intakeHint}>Pick a deity for this life sector — roster still to lock.</Text>
          </GlassCard>
        ))
      ) : (
        <>
          <Text style={styles.sectionTitle}>DMES</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.carousel}
          >
            {GEARS.map((gear) => (
              <DeityCard
                key={gear}
                gear={gear as GearId}
                rows={rows}
                progressFor={progressFor}
                onFocus={(row, tier, g) => setFocus({ kind: 'deity', row, tier, gear: g })}
                onOpenIntake={setIntakeTool}
              />
            ))}
          </ScrollView>

          <Text style={styles.sectionTitle}>MALA-D&rsquo;s</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.carousel}
          >
            {GEARS.map((gear) => (
              <MaladyCard
                key={gear}
                gear={gear as GearId}
                rowFor={rowFor}
                onFocus={(row) => setFocus({ kind: 'malady', row })}
              />
            ))}
          </ScrollView>

          <Text style={styles.sectionTitle}>SWAPS</Text>
          <SwapsSection
            swaps={compassConfig.swaps}
            rowFor={rowFor}
            progressFor={progressFor}
            onAdd={addSwap}
            onUpdate={updateSwap}
            onRemove={removeSwap}
          />

          {!compassConfig.shapeEnabled ? (
            <Pressable onPress={requestUnlockShape} style={styles.lockedHintRow}>
              <Text style={styles.lockedHintText}>🔒 SHAPE ring — unlocked by acquiring ASTRA</Text>
            </Pressable>
          ) : null}
        </>
      )}
    </ScrollView>
    {focus ? (
      <CardFocus
        focus={focus}
        game={compassConfig.game}
        progressFor={progressFor}
        picksFor={(toolId) => compassConfig.principleProgress[toolId]?.picks ?? []}
        onToggle={togglePrinciple}
        onLog={(toolId, idx) => logPrincipleToday(toolId, idx, todayStr)}
        onClose={() => setFocus(null)}
      />
    ) : null}
    </Atmos>
  );
}

function ScoreCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  const styles = useStyles();
  return (
    <GlassCard style={styles.scoreCard}>
      <Text style={styles.scoreLabel}>{label}</Text>
      <Text style={styles.scoreValue}>{value}</Text>
      <Text style={styles.scoreHint}>{hint}</Text>
    </GlassCard>
  );
}

/** Cinematic portrait card — deity or malady. Falls back to a monogram frame
 *  with an "ART PENDING" chip when the portrait hasn't been generated yet. */
function Placard({
  name,
  subtitle,
  image,
  variant,
}: {
  name: string;
  subtitle?: string;
  image?: ImageSourcePropType;
  variant: 'deity' | 'malady';
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const nameColor = variant === 'malady' ? '#f08a82' : '#f3ead7'; // over portrait scrim: mode-independent
  return (
    <View style={styles.placard}>
      {image ? (
        <Image source={image} style={styles.placardImg} resizeMode="cover" />
      ) : (
        <View style={[styles.placardImg, styles.placardPlaceholder]}>
          <Text style={styles.placardMono}>{name.charAt(0)}</Text>
          <View style={styles.pendingChip}>
            <Text style={styles.pendingChipText}>ART PENDING</Text>
          </View>
        </View>
      )}
      <View style={styles.placardPlate}>
        <Text style={[styles.placardName, { color: nameColor }]}>{name}</Text>
        {subtitle ? <Text style={styles.placardSubtitle}>{subtitle}</Text> : null}
      </View>
    </View>
  );
}

function StatTile({ value, label, small }: { value: string; label: string; small?: boolean }) {
  const styles = useStyles();
  return (
    <GlassCard style={styles.statTile}>
      <Text style={[styles.statValue, small && styles.statValueSmall]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </GlassCard>
  );
}

/** A tappable deity/malady disc — real portrait when available (with a
 *  knob-driven black veil), else a monogram. */
function Disc({
  tone,
  art,
  label,
  veil,
  onPress,
}: {
  tone: 'deity' | 'malady';
  art?: ImageSourcePropType;
  label: string;
  veil: number;
  onPress: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const accent = tone === 'malady' ? c.danger : c.primary;
  return (
    <View style={styles.discCol}>
      <Pressable onPress={onPress} style={styles.disc}>
        {art ? (
          <>
            <Image source={art} style={styles.discImg} resizeMode="cover" />
            {veil > 0 ? <View style={[styles.discVeil, { opacity: veil }]} /> : null}
          </>
        ) : (
          <Text style={[styles.discMono, { color: accent }]}>{label.charAt(0)}</Text>
        )}
      </Pressable>
      <Text style={[styles.discName, { color: tone === 'malady' ? c.danger : c.ink }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

/** The −4..+4 track between the two discs, with the position knob. */
function MiniSpectrum({ position }: { position: number }) {
  const { c } = useTheme();
  const styles = useStyles();
  const pct = ((position + 4) / 8) * 100;
  const color = position > 0 ? c.primary : position < -2 ? c.danger : c.slate;
  return (
    <View style={styles.spec}>
      <View style={styles.specTrack} />
      <View style={styles.specTicks}>
        {Array.from({ length: 9 }).map((_, i) => (
          <View key={i} style={styles.specTick} />
        ))}
      </View>
      <View style={[styles.specKnob, { left: `${pct}%`, backgroundColor: color }]}>
        <Text style={styles.specKnobText}>{position > 0 ? `+${position}` : `${position}`}</Text>
      </View>
    </View>
  );
}

// ── Flip card (front placard ⟷ back profile), matching the MoyRi artifact ──

const SWAP_ROWS: { key: 'S' | 'W' | 'A' | 'P'; label: string }[] = [
  { key: 'S', label: 'Situation — what sets it off' },
  { key: 'W', label: 'Want — the need it feeds (Keep/Gain/Change a Drive)' },
  { key: 'A', label: 'Action — the behavior it runs' },
  { key: 'P', label: 'Payoff — what you get, and how else' },
];

function FlipCard({ front, back, autoFlip }: { front: React.ReactNode; back: React.ReactNode; autoFlip?: boolean }) {
  const styles = useStyles();
  const [flipped, setFlipped] = useState(false);
  const anim = useRef(new Animated.Value(0)).current;
  const [backH, setBackH] = useState(0);
  const toggle = () => {
    setFlipped((f) => {
      Animated.timing(anim, { toValue: f ? 0 : 1, duration: 480, useNativeDriver: false }).start();
      return !f;
    });
  };
  // One-click open: as soon as the card is focused it turns to the back on its
  // own — no second tap needed to reach the selections.
  useEffect(() => {
    if (!autoFlip) return;
    const id = setTimeout(toggle, 180);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const frontRotate = anim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });
  const backRotate = anim.interpolate({ inputRange: [0, 1], outputRange: ['180deg', '360deg'] });
  return (
    <Pressable onPress={toggle}>
      <View style={[styles.flipStage, flipped && backH ? { height: backH } : null]}>
        <Animated.View style={[styles.face, { transform: [{ perspective: 1600 }, { rotateY: frontRotate }] }]}>
          {front}
        </Animated.View>
        <Animated.View
          style={[styles.face, styles.faceBack, { transform: [{ perspective: 1600 }, { rotateY: backRotate }] }]}
        >
          <View onLayout={(e) => setBackH(e.nativeEvent.layout.height)}>{back}</View>
        </Animated.View>
      </View>
    </Pressable>
  );
}

function BackHdr({ art, mono, name, sub, tone, seatId }: { art?: ImageSourcePropType; mono: string; name: string; sub: string; tone: 'deity' | 'malady'; seatId?: string }) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <View style={styles.backhdr}>
      {tone === 'deity' && seatId && portraitFor(seatId) ? (
        <Medallion id={seatId} size={44} />
      ) : (
      <View style={styles.backhdrDisc}>
        {art ? (
          <Image source={art} style={styles.backhdrImg} resizeMode="cover" />
        ) : (
          <Text style={[styles.discMono, { color: tone === 'malady' ? c.danger : c.primary }]}>{mono}</Text>
        )}
      </View>
      )}
      <View style={{ flex: 1 }}>
        <Text style={styles.backhdrName}>{name}</Text>
        <Text style={styles.backhdrAb}>{sub}</Text>
      </View>
    </View>
  );
}

function DeityFront({ row, tier, gear }: { row: Row; tier: TierId; gear: GearId }) {
  const styles = useStyles();
  const info = getDeity(row.toolId);
  return (
    <>
      <Placard name={row.toolName} subtitle={info.ability} image={dmtArt(row.toolId)} variant="deity" />
      <Text style={styles.haveit}>
        {TIER_LABEL[tier]} skillset of {GEAR_META[gear].name.toUpperCase()} — every tool is open to use from day one; how the
        later seats are scored is still to be decided.
      </Text>
      <Text style={styles.flipnote}>↻ tap the card for the full profile</Text>
    </>
  );
}

function AcquisitionTiers({ steps }: { steps: [string, string, string] }) {
  const { t } = useTheme();
  const styles = useStyles();
  const tiers = acquisitionTiers(steps);
  const [sel, setSel] = useState<number[]>([1, 1, 1, 1]);
  return (
    <>
      {tiers.map((t, ti) => (
        <View key={ti} style={styles.tierrow}>
          <Text style={styles.sectSmall}>{t.tier}</Text>
          <View style={styles.opts}>
            {t.opts.map((o, oi) => (
              <Pressable
                key={oi}
                style={[styles.opt, sel[ti] === oi && styles.optSel]}
                onPress={() => setSel((s) => s.map((v, idx) => (idx === ti ? oi : v)))}
              >
                <View style={[styles.optDot, sel[ti] === oi && styles.optDotSel]} />
                <Text style={styles.optLabel}>{o.label}</Text>
                <Text style={styles.optDiff}>{o.diff}</Text>
              </Pressable>
            ))}
          </View>
          <Text style={styles.kyrs}>→ picked → recurring KYRS task</Text>
        </View>
      ))}
    </>
  );
}

function DeityBack({
  row,
  picks,
  stats,
  game,
  onToggle,
  onLog,
}: {
  row: Row;
  picks: string[];
  stats: ProgressStats;
  game: GameSettings;
  onToggle: (id: string) => void;
  onLog: (id: string) => void;
}) {
  const styles = useStyles();
  const info = getDeity(row.toolId);
  const levelPrinciples = principlesForLevel(row.toolId, stats.level);
  // Picks are open any day; the weekly pick is prompted at the Sunday-evening boundary.
  const pickPrompt = isPickPrompt(game);
  return (
    <>
      <BackHdr art={dmtArt(row.toolId)} mono={row.toolName.charAt(0)} name={row.toolName} sub={info.ability} tone="deity" seatId={row.toolId} />

      {/* Live acquisition readout */}
      <View style={styles.acqBar}>
        <View style={styles.acqBarTop}>
          <Text style={styles.acqPts}>
            LVL {stats.level} · {stats.points} / {stats.levelTarget} {POINTS_UNIT}
          </Text>
          {stats.maxed ? (
            <Text style={styles.acqDate}>maxed</Text>
          ) : stats.projectedDate ? (
            <Text style={styles.acqDate}>
              {stats.secured ? `L${stats.level} by ` : 'acquire by '}{stats.projectedDate}
            </Text>
          ) : null}
        </View>
        <View style={styles.acqTrack}>
          <View style={[styles.acqFill, { width: `${Math.round(stats.pct * 100)}%` }]} />
        </View>
        <Text style={styles.acqBreak}>
          {stats.steps} steps · {stats.todayCount} ordered days · {stats.aims} aims · {stats.goals} goals
        </Text>
        {stats.seasonEnd && !stats.maxed ? (
          <Text style={styles.acqBreak}>season resets {stats.seasonEnd} · banked levels never reset</Text>
        ) : null}
      </View>

      <Text style={styles.sect}>PRINCIPLES · LVL {stats.level} — tick the ones you live by. Mark ✓ each day.</Text>
      <Text style={styles.pickLock}>
        {pickPrompt ? '🔔 Sunday evening — close the week, then set next week’s picks' : 'Change your picks any day · the weekly pick is prompted Sunday evening'}
      </Text>
      <View style={styles.stepList}>
        {levelPrinciples.map((o) => {
          const active = picks.includes(o.id);
          const doneToday = stats.doneToday.includes(o.id);
          return (
            <View key={o.id} style={[styles.stepOpt, active && styles.stepOptSel]}>
              <Pressable
                style={styles.stepPickRow}
                onPress={() => onToggle(o.id)}
                hitSlop={4}
              >
                <View style={[styles.stepCheck, active && styles.stepCheckOn]}>
                  {active ? <Text style={styles.stepCheckMark}>✓</Text> : null}
                </View>
                <View style={[styles.stepOptText]}>
                  <Text style={styles.stepOptTitle}>{o.title}</Text>
                  <Text style={styles.stepOptDetail}>{o.detail}</Text>
                </View>
                <Text style={[styles.stepOptFreq, active && styles.stepOptFreqSel]}>{o.perWeek}×/wk</Text>
              </Pressable>
              {active ? (
                <Pressable
                  style={[styles.doneBtn, doneToday && styles.doneBtnOn]}
                  onPress={() => onLog(o.id)}
                  hitSlop={4}
                >
                  <Text style={[styles.doneBtnText, doneToday && styles.doneBtnTextOn]}>
                    {doneToday ? '✓ done today (+1)' : 'mark done today'}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          );
        })}
      </View>
      <Text style={styles.kyrs}>→ aims &amp; goals are where the points are · stack more to arrive sooner</Text>
      <View style={styles.chat}>
        <Text style={styles.chatText}>Chat with {row.toolName}</Text>
      </View>
      <Text style={styles.flipnote}>↻ tap to flip back</Text>
    </>
  );
}

function MaladyFront({ row }: { row: Row }) {
  const styles = useStyles();
  return (
    <>
      <Placard name={row.maladyName} subtitle="the pattern this slot fights" image={maladyArt(row.maladyId, row.toolId)} variant="malady" />
      <Text style={styles.flipnote}>↻ tap the card for symptoms{`${''}`}</Text>
    </>
  );
}

function MaladyBack({ row, deityName, entry, showSwap }: { row: Row; deityName: string; entry?: SwapEntry; showSwap: boolean }) {
  const styles = useStyles();
  return (
    <>
      <BackHdr
        art={maladyArt(row.maladyId, row.toolId)}
        mono={row.maladyName.charAt(0)}
        name={row.maladyName}
        sub="Mala-D · the −4 pole"
        tone="malady"
      />
      <Text style={styles.sect}>WHY IT EXISTS</Text>
      <Text style={styles.why}>{entry?.why ?? ''}</Text>
      <Text style={styles.sect}>SYMPTOMS</Text>
      <GlassCard style={styles.symlist}>
        {(entry?.symptoms ?? []).map((s) => (
          <Text key={s} style={styles.symLine}>
            {'•  '}
            {s}
          </Text>
        ))}
      </GlassCard>
      {showSwap ? (
        <>
          <Text style={styles.sect}>SWAP — trade it for the deity, line by line</Text>
          <View style={styles.swaphdr}>
            <Text style={styles.swapHdrMal}>◂ WAS · the malady</Text>
            <Text style={styles.swapHdrDei}>NOW · {deityName} ▸</Text>
          </View>
          {SWAP_ROWS.map((r) => {
            const side = entry?.swap[r.key];
            return (
              <View key={r.key} style={styles.swapgroup}>
                <View style={styles.swapk}>
                  <Text style={styles.lt}>{r.key}</Text>
                  <Text style={styles.swapkText}>{r.label}</Text>
                </View>
                <View style={styles.swaptwo}>
                  <SelectBox value={side?.mal ?? '—'} />
                  <Text style={styles.arr}>→</Text>
                  <SelectBox value={side?.dei ?? '—'} accent />
                </View>
              </View>
            );
          })}
        </>
      ) : null}
      <View style={[styles.chat, styles.chatGhost]}>
        <Text style={styles.chatGhostText}>Chat with {row.maladyName}</Text>
      </View>
      <Text style={styles.flipnote}>↻ tap to flip back</Text>
    </>
  );
}

function SelectBox({ value, accent }: { value: string; accent?: boolean }) {
  const styles = useStyles();
  return (
    <View style={[styles.selectBox, accent && styles.selectBoxAccent]}>
      <Text style={styles.selectText}>{value}</Text>
      <Text style={styles.selectCaret}>▾</Text>
    </View>
  );
}

const SWAP_ROW_META: { key: 'S' | 'W' | 'A' | 'P'; label: string }[] = [
  { key: 'S', label: 'Situation — what sets it off' },
  { key: 'W', label: 'Want — the need it feeds' },
  { key: 'A', label: 'Action — the behavior that runs' },
  { key: 'P', label: 'Payoff — what it pays out' },
];

/**
 * The malady's WHY / SYMPTOMS / SWAP / SIGNAL — the CHANGE-adjacent card back.
 * Falls back to nothing (renders null) for maladies without authored content
 * yet, rather than showing an empty shell.
 */
function SwapSection({ row }: { row: Row }) {
  const { t } = useTheme();
  const styles = useStyles();
  const entry = getSwapEntry(row.toolId, row.maladyId);
  if (!entry) return null;
  return (
    <View style={styles.swapWrap}>
      <GlassCard style={styles.card}>
        <Text style={styles.cardLabel}>WHY IT EXISTS</Text>
        <Text style={styles.cardBody}>{entry.why}</Text>
      </GlassCard>

      <GlassCard style={styles.card}>
        <Text style={styles.cardLabel}>SYMPTOMS</Text>
        {entry.symptoms.map((s) => (
          <Text key={s} style={styles.symptomLine}>
            {'• '}
            {s}
          </Text>
        ))}
      </GlassCard>

      <GlassCard style={styles.card}>
        <Text style={styles.cardLabel}>SWAP — trade it for {row.toolName}</Text>
        <View style={styles.swapHeaderRow}>
          <Text style={styles.swapHeaderMal}>WAS · {entry.maladyName}</Text>
          <Text style={styles.swapHeaderDei}>NOW · {row.toolName}</Text>
        </View>
        {SWAP_ROW_META.map(({ key, label }) => {
          const side = entry.swap[key];
          return (
            <View key={key} style={styles.swapGroup}>
              <Text style={styles.swapRowLabel}>
                {key} — {label}
              </Text>
              <View style={styles.swapTwo}>
                <Text style={styles.swapMalText}>{side.mal}</Text>
                <Text style={styles.swapArrow}>{'→'}</Text>
                <Text style={styles.swapDeiText}>{side.dei}</Text>
              </View>
            </View>
          );
        })}
        <Text style={styles.driveHint}>
          The Want row is one of the four Drives — Self, Body, Status, Possessions — each as Keep, Gain, or Change.
        </Text>
      </GlassCard>

      <GlassCard style={styles.card}>
        <Text style={styles.cardLabel}>HOW THIS IS TRACKED</Text>
        <Text style={styles.cardBody}>{entry.signal.automatic}</Text>
        <Text style={styles.signalFallback}>
          {entry.signal.fallback.startsWith('None')
            ? 'No self-report needed — this reads entirely from your usage.'
            : entry.signal.fallback}
        </Text>
      </GlassCard>
    </View>
  );
}

// ── MoyRi ORDER surface: DMES / MALA-D's / SWAPS ──────────────────────────

/** Short acquisition-tier names (STEP/TODAY/AIM/GOAL) — the four progress dots. */
const STAGE_LABELS = TIER_LABELS.map((l) => l.split('·')[0].trim());

/** Every malady across the roster, for the SWAPS malady picker. */
const MALADY_OPTIONS = DMT_TOOLS.flatMap((t) =>
  t.maladies.map((m) => ({ value: `${t.id}:${m.id}`, label: `${m.name} · ${t.name}` })),
);
/** Every deity, for the SWAPS deity picker. */
const DEITY_OPTIONS = DMT_TOOLS.map((t) => ({ value: t.id as DmtToolId, label: t.name }));

/** Zoomed-in "main card" overlay. The tapped card grows into focus and turns
 *  to the back on its own (one click) — the step picker for a deity; WHY /
 *  SYMPTOMS / SWAP for a malady. */
function CardFocus({
  focus,
  game,
  progressFor,
  picksFor,
  onToggle,
  onLog,
  onClose,
}: {
  focus: FocusTarget;
  game: GameSettings;
  progressFor: (toolId: DmtToolId) => ProgressStats;
  picksFor: (toolId: DmtToolId) => string[];
  onToggle: (toolId: DmtToolId, id: string) => void;
  onLog: (toolId: DmtToolId, id: string) => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  const scale = useRef(new Animated.Value(0.82)).current;
  useEffect(() => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: false, friction: 8, tension: 90 }).start();
  }, [scale]);

  const front =
    focus.kind === 'deity' ? (
      <DeityFront row={focus.row} tier={focus.tier} gear={focus.gear} />
    ) : (
      <MaladyFront row={focus.row} />
    );
  const back =
    focus.kind === 'deity' ? (
      <DeityBack
        row={focus.row}
        picks={picksFor(focus.row.toolId)}
        stats={progressFor(focus.row.toolId)}
        game={game}
        onToggle={(i) => onToggle(focus.row.toolId, i)}
        onLog={(i) => onLog(focus.row.toolId, i)}
      />
    ) : (
      <MaladyBack
        row={focus.row}
        deityName={focus.row.toolName}
        entry={getSwapEntry(focus.row.toolId, focus.row.maladyId)}
        showSwap={false}
      />
    );

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.focusBackdrop} onPress={onClose}>
        <Animated.View style={[styles.focusCard, { transform: [{ scale }] }]}>
          {/* Absorbs taps so tapping the card (or its padding) never closes the
              overlay — only the backdrop does. The FlipCard inside owns its own
              tap-to-flip. */}
          <Pressable onPress={() => {}} style={styles.focusInner}>
            <View style={styles.focusHeader}>
              <Text style={styles.focusHint}>tap to flip back</Text>
              <Pressable onPress={onClose} hitSlop={10}>
                <Text style={styles.focusClose}>✕</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.focusScroll} showsVerticalScrollIndicator={false}>
              <FlipCard front={front} back={back} autoFlip />
            </ScrollView>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

/** A square portrait for a carousel card, with a monogram fallback. */
function CardImage({ art, name, malady }: { art?: ImageSourcePropType; name: string; malady?: boolean }) {
  const { t, c } = useTheme();
  const styles = useStyles();
  return art ? (
    <Image source={art} style={[styles.cardImg, malady && { borderWidth: 2, borderColor: t.maladyRed }]} resizeMode="cover" />
  ) : (
    <View style={[styles.cardImg, styles.cardImgPlaceholder]}>
      <Text style={[styles.cardMono, malady ? { color: c.danger } : null]}>{name.charAt(0)}</Text>
    </View>
  );
}

/** The four acquisition-tier dots + highest-rung label, driven by the live
 *  point engine (read-only readout — points are earned by logging principles,
 *  not by tapping here). Shows the projected acquisition date beneath. */
function StageDots({ stats }: { stats: ProgressStats }) {
  const styles = useStyles();
  const label = stats.secured ? 'SECURED' : STAGE_LABELS[stats.tierIdx];
  return (
    <View style={styles.stageRow}>
      <View style={styles.stageTop}>
        <Text style={styles.stageLabel}>{label}</Text>
        <View style={styles.stageDots}>
          {STAGE_LABELS.map((_, i) => (
            <View key={i} style={[styles.dot, i < stats.tierIdx && styles.dotFilled]} />
          ))}
        </View>
      </View>
      {stats.points > 0 ? (
        <Text style={styles.stagePts} numberOfLines={1}>
          L{stats.level} · {stats.points}/{stats.levelTarget}
          {stats.maxed ? ' · maxed' : stats.projectedDate ? ` · by ${stats.projectedDate}` : ''}
        </Text>
      ) : null}
    </View>
  );
}

/** One gear's deity placard: name, portrait, ◀ GEAR: TIER ▶ tier-cycle, and
 *  the acquisition-stage dots. Tapping the portrait opens the deity detail
 *  (or the intake, if this line hasn't been placed yet). */
function DeityCard({
  gear,
  rows,
  progressFor,
  onFocus,
  onOpenIntake,
}: {
  gear: GearId;
  rows: Row[] | null;
  progressFor: (toolId: DmtToolId) => ProgressStats;
  onFocus: (row: Row, tier: TierId, gear: GearId) => void;
  onOpenIntake: (tool: DmtTool) => void;
}) {
  const styles = useStyles();
  const [tier, setTier] = useState<TierId>('mortal');
  const defaulted = useRef(false);

  // Default to the highest tier the user has begun (points > 0),
  // once — after that, the arrows own the selection.
  useEffect(() => {
    if (defaulted.current) return;
    defaulted.current = true;
    let best: TierId = 'mortal';
    for (const t of TIER_ORDER) {
      if (progressFor(GEAR_TIERS[gear][t]).points > 0) best = t;
    }
    setTier(best);
  }, [gear, progressFor]);

  const toolId = GEAR_TIERS[gear][tier];
  const row = rows?.find((r) => r.toolId === toolId);
  const name = row?.toolName ?? getTool(toolId).name;
  const idx = TIER_ORDER.indexOf(tier);
  const cycle = (dir: number) => setTier(TIER_ORDER[(idx + dir + TIER_ORDER.length) % TIER_ORDER.length]);

  return (
    <GlassCard style={styles.carouselCard}>
      <Text style={styles.carouselName} numberOfLines={1}>{name}</Text>
      <Pressable
        onPress={() => {
          if (!row) return;
          if (!row.intakeTaken) onOpenIntake(getTool(toolId));
          else onFocus(row, tier, gear);
        }}
      >
        {portraitFor(toolId) ? (
          <View style={styles.medalWrap}>
            <Medallion
              id={toolId}
              size={112}
              state={!row?.intakeTaken ? 'veiled' : progressFor(toolId).secured ? 'secured' : 'active'}
              glow={!!row?.intakeTaken && progressFor(toolId).secured}
            />
          </View>
        ) : (
          <CardImage art={dmtArt(toolId)} name={name} />
        )}
      </Pressable>
      <View style={styles.tierArrowRow}>
        <Pressable onPress={() => cycle(-1)} hitSlop={8}>
          <Text style={styles.tierArrow}>◀</Text>
        </Pressable>
        <Text style={styles.tierArrowLabel} numberOfLines={1}>
          {GEAR_META[gear].name.toUpperCase()}: {TIER_LABEL[tier]}
        </Text>
        <Pressable onPress={() => cycle(1)} hitSlop={8}>
          <Text style={styles.tierArrow}>▶</Text>
        </Pressable>
      </View>
      {row ? (
        !row.intakeTaken ? (
          <Text style={styles.cardHint} numberOfLines={1}>take intake to place →</Text>
        ) : (
          <StageDots stats={progressFor(toolId)} />
        )
      ) : null}
    </GlassCard>
  );
}

/** One gear's malady card: cycles the flat list of that gear's maladies (not
 *  split by tier). Tapping the portrait zooms the card into focus and flips it
 *  to the malady's WHY / SYMPTOMS / SWAP — same flip as the deity cards. */
function MaladyCard({
  gear,
  rowFor,
  onFocus,
}: {
  gear: GearId;
  rowFor: (toolId: DmtToolId) => Row | undefined;
  onFocus: (row: Row) => void;
}) {
  const styles = useStyles();
  const list = TIER_ORDER.flatMap((t) => {
    const tId = GEAR_TIERS[gear][t];
    return getTool(tId).maladies.map((m) => ({ toolId: tId, maladyId: m.id, name: m.name }));
  });
  const [i, setI] = useState(0);
  const cur = list[i % list.length];
  const step = (dir: number) => setI((p) => (p + dir + list.length) % list.length);

  if (!cur) return null;

  const openFlip = () => {
    const base = rowFor(cur.toolId);
    if (!base) return;
    // Focus the *specific* malady being cycled, not the tool's stored subtype.
    onFocus({ ...base, maladyId: cur.maladyId, maladyName: cur.name });
  };

  return (
    <GlassCard style={styles.carouselCard}>
      <Text style={styles.carouselName} numberOfLines={1}>{cur.name}</Text>
      <Pressable onPress={openFlip}>
        <CardImage art={maladyArt(cur.maladyId, cur.toolId)} name={cur.name} malady />
      </Pressable>
      <View style={styles.tierArrowRow}>
        <Pressable onPress={() => step(-1)} hitSlop={8}>
          <Text style={styles.tierArrow}>◀</Text>
        </Pressable>
        <Text style={styles.tierArrowLabel} numberOfLines={1}>{GEAR_META[gear].name.toUpperCase()}</Text>
        <Pressable onPress={() => step(1)} hitSlop={8}>
          <Text style={styles.tierArrow}>▶</Text>
        </Pressable>
      </View>
      <Text style={styles.cardHint} numberOfLines={1}>tap image for aspects →</Text>
    </GlassCard>
  );
}

/** A full-screen option picker used by the SWAPS add flow. */
function PickerModal({
  visible,
  title,
  options,
  onSelect,
  onClose,
}: {
  visible: boolean;
  title: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.modalBackdrop} onPress={onClose}>
        <GlassCard style={styles.modalSheet}>
          <Text style={styles.modalTitle}>{title}</Text>
          <ScrollView style={{ maxHeight: 380 }}>
            {options.map((o) => (
              <Pressable
                key={o.value}
                style={styles.modalItem}
                onPress={() => {
                  onSelect(o.value);
                  onClose();
                }}
              >
                <Text style={styles.modalItemText}>{o.label}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </GlassCard>
      </Pressable>
    </Modal>
  );
}

/** One active swap, as a single combined card: malady (left) → position bar →
 *  deity (right) on the front; tapping anywhere flips it to the malady→deity
 *  SWAP trade table (the same "backwards placard" the malady gives). ON/OFF +
 *  delete sit above the flip so they never trigger it. */
function SwapCard({
  swap,
  rowFor,
  progressFor,
  onToggle,
  onRemove,
}: {
  swap: CustomSwap;
  rowFor: (toolId: DmtToolId) => Row | undefined;
  progressFor: (toolId: DmtToolId) => ProgressStats;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [tId, rawM] = swap.maladyKey.split(':') as [DmtToolId, string];
  const mId = normalizeMaladyId(tId, rawM);
  const maladyName = getTool(tId).maladies.find((m) => m.id === mId)?.name ?? mId;
  const deity = getTool(swap.deityToolId);
  const deityRow = rowFor(swap.deityToolId);
  const pos = deityRow?.state.displayed ?? 0;
  const dstats = progressFor(swap.deityToolId);
  const status = dstats.secured ? 'SECURED' : STAGE_LABELS[dstats.tierIdx];
  const tinfo = tierOf(swap.deityToolId);
  const tierLabel = tinfo ? TIER_LABEL[tinfo.tier] : '';

  const base = rowFor(tId);
  const malRow: Row | null = base ? { ...base, maladyId: mId, maladyName } : null;
  const entry = getSwapEntry(tId, mId);
  const malArt = maladyArt(mId, tId);
  const deiArt = dmtArt(swap.deityToolId);

  const front = (
    <View>
      {tierLabel ? <Text style={styles.swapTier}>{tierLabel}</Text> : null}
      <View style={styles.swapFrontRow}>
        <View style={styles.swapEndCol}>
          <View style={styles.swapEndImg}>
            {malArt ? (
              <Image source={malArt} style={styles.swapEndImgInner} resizeMode="cover" />
            ) : (
              <Text style={[styles.swapEndMono, { color: c.danger }]}>{maladyName.charAt(0)}</Text>
            )}
          </View>
          <Text style={[styles.swapEndName, { color: c.danger }]} numberOfLines={1}>{maladyName}</Text>
        </View>
        <MiniSpectrum position={pos} />
        <View style={styles.swapEndCol}>
          <View style={styles.swapEndImg}>
            {deiArt ? (
              <Image source={deiArt} style={styles.swapEndImgInner} resizeMode="cover" />
            ) : (
              <Text style={[styles.swapEndMono, { color: c.primary }]}>{deity.name.charAt(0)}</Text>
            )}
          </View>
          <Text style={[styles.swapEndName, { color: c.primary }]} numberOfLines={1}>{deity.name}</Text>
        </View>
      </View>
      <Text style={styles.swapStatus}>{status} · {pos > 0 ? `+${pos}` : pos}</Text>
      <Text style={styles.flipnote}>↻ tap to set up the swap</Text>
    </View>
  );

  const back = malRow ? (
    <MaladyBack row={malRow} deityName={deity.name} entry={entry} showSwap />
  ) : (
    <Text style={styles.miniBody}>Swap not available.</Text>
  );

  return (
    <GlassCard style={[styles.swapRow, !swap.enabled && { opacity: 0.5 }]}>
      <View style={styles.swapRowHead}>
        <Pressable onPress={onToggle} style={[styles.swapPill, swap.enabled && styles.swapPillOn]}>
          <Text style={[styles.swapPillText, swap.enabled && styles.swapPillTextOn]}>{swap.enabled ? 'ON' : 'OFF'}</Text>
        </Pressable>
        <Pressable onPress={onRemove} hitSlop={6}>
          <Text style={styles.swapRemove}>✕</Text>
        </Pressable>
      </View>
      <FlipCard front={front} back={back} />
    </GlassCard>
  );
}

/** The SWAPS section: active malady→deity swaps (combined flip cards) plus an
 *  add flow with two pickers. Independently on/off per swap — distinct from
 *  acquisition, which is the default-on surface above. */
function SwapsSection({
  swaps,
  rowFor,
  progressFor,
  onAdd,
  onUpdate,
  onRemove,
}: {
  swaps: CustomSwap[];
  rowFor: (toolId: DmtToolId) => Row | undefined;
  progressFor: (toolId: DmtToolId) => ProgressStats;
  onAdd: (maladyKey: string, deityToolId: DmtToolId) => void;
  onUpdate: (id: string, patch: Partial<Omit<CustomSwap, 'id'>>) => void;
  onRemove: (id: string) => void;
}) {
  const { c, neu } = useTheme();
  const styles = useStyles();
  const [adding, setAdding] = useState(false);
  const [draftMalady, setDraftMalady] = useState<string | null>(null);
  const [draftDeity, setDraftDeity] = useState<DmtToolId | null>(null);
  const [picker, setPicker] = useState<null | 'malady' | 'deity'>(null);

  const maladyName = (key: string) => {
    const [tId, rawM] = key.split(':');
    const mId = normalizeMaladyId(tId, rawM);
    return getTool(tId as DmtToolId).maladies.find((m) => m.id === mId)?.name ?? mId;
  };
  const maladyLabel = draftMalady
    ? `${maladyName(draftMalady)} · ${getTool(draftMalady.split(':')[0] as DmtToolId).name}`
    : 'Pick a malady';
  const deityLabel = draftDeity ? getTool(draftDeity).name : 'Pick a deity';

  const resetDraft = () => {
    setDraftMalady(null);
    setDraftDeity(null);
    setAdding(false);
  };
  const confirmAdd = () => {
    if (!draftMalady || !draftDeity) return;
    onAdd(draftMalady, draftDeity);
    resetDraft();
  };

  return (
    <View style={styles.swapsWrap}>
      {swaps.length === 0 && !adding ? (
        <Text style={styles.swapsEmpty}>
          No swaps yet. Acquisition runs by default above — add a swap to trade a malady for a deity, line by line.
        </Text>
      ) : null}

      {swaps.map((s) => (
        <SwapCard
          key={s.id}
          swap={s}
          rowFor={rowFor}
          progressFor={progressFor}
          onToggle={() => onUpdate(s.id, { enabled: !s.enabled })}
          onRemove={() => onRemove(s.id)}
        />
      ))}

      {adding ? (
        <GlassCard style={styles.swapRow}>
          <View style={styles.swapTwo}>
            <Pressable style={styles.selectBox} onPress={() => setPicker('malady')}>
              <Text style={styles.selectText} numberOfLines={1}>{maladyLabel}</Text>
              <Text style={styles.selectCaret}>▾</Text>
            </Pressable>
            <Text style={styles.arr}>→</Text>
            <Pressable style={[styles.selectBox, styles.selectBoxAccent]} onPress={() => setPicker('deity')}>
              <Text style={styles.selectText} numberOfLines={1}>{deityLabel}</Text>
              <Text style={styles.selectCaret}>▾</Text>
            </Pressable>
          </View>
          <View style={styles.swapAddActions}>
            <Pressable onPress={resetDraft} style={styles.addSwapBtn}>
              <Text style={styles.addSwapText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={confirmAdd}
              disabled={!draftMalady || !draftDeity}
              style={[styles.addSwapBtn, styles.addSwapBtnPrimary, (!draftMalady || !draftDeity) && { opacity: 0.4 }]}
            >
              <Text style={[styles.addSwapText, styles.addSwapTextPrimary]}>Add swap</Text>
            </Pressable>
          </View>
        </GlassCard>
      ) : (
        <Pressable onPress={() => setAdding(true)} style={styles.addSwapBtn}>
          <Text style={styles.addSwapText}>+ Add swap</Text>
        </Pressable>
      )}

      <PickerModal
        visible={picker !== null}
        title={picker === 'malady' ? 'Pick a malady' : 'Pick a deity'}
        options={picker === 'malady' ? MALADY_OPTIONS : DEITY_OPTIONS}
        onSelect={(v) => (picker === 'malady' ? setDraftMalady(v) : setDraftDeity(v as DmtToolId))}
        onClose={() => setPicker(null)}
      />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  container: { padding: 16, gap: 12 },
  scoreRow: { flexDirection: 'row', gap: 12 },
  scoreCard: {
    flex: 1,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...neu.raised,
  },
  scoreLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: c.textFaint },
  scoreValue: { fontSize: 24, fontWeight: '800', color: c.slate },
  scoreHint: { fontSize: 10, color: c.textFaint },
  loading: { textAlign: 'center', color: c.textFaint, paddingVertical: 40 },
  gearGroupWrap: { gap: 10, marginBottom: 4 },
  gearGroupHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  gearGroupTitle: { flex: 1, fontSize: 11, fontWeight: '700', letterSpacing: 2, textTransform: 'uppercase', color: c.textFaint },
  swapPill: {
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 12,
    paddingVertical: 5,
    ...neu.raisedSm,
  },
  swapPillOn: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  swapPillText: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.textFaint },
  swapPillTextOn: { color: t.onGold },
  lockedHintRow: {
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingHorizontal: 14,
    paddingVertical: 12,
    alignItems: 'center',
    ...neu.inset,
  },
  lockedHintText: { fontSize: 12, fontWeight: '700', color: c.textFaint },
  intakePrompt: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 16,
    ...neu.raisedSm,
  },
  intakeName: { fontSize: 14, fontWeight: '700', color: c.slate },
  intakeHint: { fontSize: 12, color: c.inkSoft },
  detail: { padding: 20, gap: 16 },
  back: { fontSize: 14, color: c.inkSoft },
  card: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 16,
    ...neu.raised,
  },
  cardLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: c.textFaint },
  malady: { fontSize: 18, fontWeight: '600', color: t.maladyText },
  cardBody: { marginTop: 4, fontSize: 14, color: c.slate },
  certified: { marginTop: 12, fontSize: 14, fontWeight: '600', color: t.onPace },
  certButton: {
    marginTop: 12,
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 20,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  certButtonText: { fontSize: 14, fontWeight: '600', color: c.slate },
  fine: { marginTop: 8, fontSize: 11, color: c.textFaint },
  tabs: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
    borderRadius: radii.pill,
    padding: 6,
  },
  tab: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: radii.pill },
  tabOn: { backgroundColor: t.gold, boxShadow: `0 0 12px ${t.goldGlow}, inset 0 1px 0 ${t.goldBright}` },
  tabText: { fontSize: 13, fontWeight: '700', letterSpacing: 1, color: c.textFaint },
  tabTextOn: { color: t.onGold },
  tabNote: { fontSize: 11.5, color: c.textFaint, textAlign: 'center', marginTop: 8, marginBottom: 4 },
  slot: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 15,
    ...neu.raised,
  },
  slotAnchor: { ...neu.raised, borderWidth: 1.5, borderColor: t.goldGlow },
  seclabel: { fontSize: 11, fontWeight: '800', letterSpacing: 1.5, color: c.textFaint, marginBottom: 12 },
  seclabelAnchor: { color: c.primary },
  slotRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  slotState: { marginTop: 9, textAlign: 'right', fontSize: 10, letterSpacing: 1.5, fontWeight: '800', color: c.textFaint },
  discCol: { alignItems: 'center', gap: 5, width: 68 },
  disc: {
    width: 46,
    height: 46,
    borderRadius: 14,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  discImg: { width: '100%', height: '100%' },
  discVeil: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000' /* black scrim over portrait art, same in both modes */ },
  discMono: { fontSize: 17, fontWeight: '800' },
  discName: { fontSize: 12, fontWeight: '700', textAlign: 'center' },
  spec: { flex: 1, height: 44, justifyContent: 'center' },
  specTrack: { position: 'absolute', left: 0, right: 0, height: 8, borderRadius: 999, backgroundColor: t.track, ...neu.inset },
  specTicks: { position: 'absolute', left: 2, right: 2, flexDirection: 'row', justifyContent: 'space-between' },
  specTick: { width: 2, height: 8, borderRadius: 2, backgroundColor: c.textFaint, opacity: 0.25 },
  specKnob: {
    position: 'absolute',
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ translateX: -15 }],
    ...neu.raisedSm,
  },
  specKnobText: { color: t.onGold, fontSize: 12, fontWeight: '800' },
  inlineCard: { marginTop: 14, gap: 14 },
  flipStage: { marginTop: 14 },
  face: {
    width: '100%',
    backgroundColor: t.glassStrong,
    borderRadius: radii.panel,
    padding: 15,
    backfaceVisibility: 'hidden',
    ...neu.raisedSm,
  },
  faceBack: { position: 'absolute', top: 0, left: 0 },
  backhdr: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 11 },
  backhdrDisc: {
    width: 44,
    height: 44,
    borderRadius: 14,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  backhdrImg: { width: '100%', height: '100%' },
  backhdrName: { fontFamily: DISPLAY_FONT, fontSize: 16, letterSpacing: 2, textTransform: 'uppercase', color: c.ink },
  backhdrAb: { fontSize: 11, color: c.textMuted, fontStyle: 'italic' },
  sect: { fontSize: 10, letterSpacing: 1.5, fontWeight: '800', color: c.textFaint, marginTop: 13, marginBottom: 6 },
  sectSmall: { fontSize: 10, letterSpacing: 1.2, fontWeight: '800', color: c.textFaint, marginTop: 12, marginBottom: 7 },
  haveit: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 11,
    fontSize: 12.5,
    lineHeight: 18,
    color: c.slate,
    ...neu.inset,
  },
  tierrow: { marginBottom: 11 },
  opts: { gap: 7 },
  opt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: 13,
    paddingVertical: 9,
    paddingHorizontal: 12,
    ...neu.raisedSm,
  },
  optSel: { borderWidth: 2, borderColor: c.primary },
  optDot: { width: 16, height: 16, borderRadius: 8, backgroundColor: t.track, ...neu.inset },
  optDotSel: { backgroundColor: c.primary },
  optLabel: { flex: 1, fontSize: 13, color: c.slate },
  optDiff: { fontSize: 10, letterSpacing: 1, fontWeight: '800', color: c.textFaint },
  kyrs: { fontSize: 11, color: c.primary, fontWeight: '700', marginTop: 8 },
  chat: { marginTop: 12, backgroundColor: c.primary, borderRadius: 13, paddingVertical: 11, alignItems: 'center', ...neu.raisedSm },
  chatText: { color: t.onGold, fontWeight: '700', fontSize: 13 },
  chatGhost: { backgroundColor: t.glass },
  chatGhostText: { color: c.primary, fontWeight: '700', fontSize: 13 },
  why: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 11,
    fontSize: 12.5,
    lineHeight: 18,
    color: c.slate,
    ...neu.inset,
  },
  symlist: { backgroundColor: t.glass, borderRadius: radii.tile, padding: 11, ...neu.inset },
  symLine: { fontSize: 12, lineHeight: 19, color: c.slate },
  swapLockedHint: {
    marginTop: 12,
    fontSize: 11.5,
    fontStyle: 'italic',
    color: c.textFaint,
    lineHeight: 16,
  },
  swaphdr: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4, marginBottom: 8 },
  swapHdrMal: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.danger },
  swapHdrDei: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.primary },
  swapgroup: { marginBottom: 11 },
  swapk: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 5 },
  lt: {
    width: 18,
    height: 18,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: c.primary,
    color: t.onGold,
    fontSize: 10,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 18,
  },
  swapkText: { flex: 1, fontSize: 10, letterSpacing: 0.3, fontWeight: '800', color: c.textFaint },
  swaptwo: { flexDirection: 'row', alignItems: 'stretch', gap: 5 },
  selectBox: {
    flex: 1,
    minHeight: 42,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: c.inputRim,
    paddingVertical: 8,
    paddingLeft: 10,
    paddingRight: 20,
    justifyContent: 'center',
  },
  selectBoxAccent: { borderColor: c.primary },
  selectText: { fontSize: 11, color: c.ink, lineHeight: 15 },
  selectCaret: { position: 'absolute', right: 9, top: 9, fontSize: 10, color: c.textFaint },
  arr: { alignSelf: 'center', color: c.primary, fontWeight: '800', fontSize: 13 },
  flipnote: { fontSize: 10.5, color: c.textFaint, textAlign: 'center', marginTop: 11 },
  placard: {
    alignSelf: 'center',
    width: 200,
    maxWidth: '100%',
    borderRadius: radii.panel,
    overflow: 'hidden',
    backgroundColor: t.navyRaised,
    ...neu.raised,
  },
  placardImg: { width: '100%', height: 200 },
  placardPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.navy },
  placardMono: { fontSize: 72, fontWeight: '800', color: t.ivoryFaint },
  pendingChip: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: 'rgba(0,0,0,0.42)',
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  pendingChipText: { fontSize: 9, letterSpacing: 1.5, fontWeight: '800', color: '#f3ead7' },
  placardPlate: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 16,
    paddingTop: 28,
    paddingBottom: 14,
    backgroundColor: 'rgba(12,16,28,0.72)',
  },
  placardName: { fontFamily: DISPLAY_FONT, fontSize: 22, letterSpacing: 2, textTransform: 'uppercase' },
  placardSubtitle: { marginTop: 3, fontSize: 12, fontStyle: 'italic', color: 'rgba(243,234,215,0.82)' /* ivorySoft over scrim */ },
  statsRow: { flexDirection: 'row', gap: 8 },
  statTile: {
    flex: 1,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingVertical: 10,
    paddingHorizontal: 4,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  statValue: { fontSize: 16, fontWeight: '800', color: c.slate },
  statValueSmall: { fontSize: 10, marginTop: 3 },
  statLabel: { fontSize: 8.5, letterSpacing: 0.6, fontWeight: '700', color: c.textFaint, marginTop: 2 },
  swapWrap: { gap: 16 },
  symptomLine: { marginTop: 4, fontSize: 13, color: c.slate, lineHeight: 18 },
  swapHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, marginBottom: 4 },
  swapHeaderMal: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.danger },
  swapHeaderDei: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.primary },
  swapGroup: { marginTop: 10 },
  swapRowLabel: { fontSize: 11, fontWeight: '700', color: c.textFaint, marginBottom: 5 },
  swapTwo: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  swapMalText: {
    flex: 1,
    fontSize: 12,
    color: c.slate,
    backgroundColor: c.glass,
    borderRadius: radii.tile,
    padding: 10,
    lineHeight: 16,
  },
  swapDeiText: {
    flex: 1,
    fontSize: 12,
    color: c.slate,
    backgroundColor: c.glass,
    borderRadius: radii.tile,
    padding: 10,
    lineHeight: 16,
    borderColor: c.primary,
    borderWidth: 1,
  },
  swapArrow: { fontSize: 14, fontWeight: '800', color: c.primary },
  driveHint: { marginTop: 10, fontSize: 10.5, color: c.textFaint, fontStyle: 'italic', lineHeight: 15 },
  signalFallback: { marginTop: 8, fontSize: 11, color: c.textFaint, fontStyle: 'italic', lineHeight: 15 },

  // ── MoyRi ORDER surface ──
  sectionTitle: { fontSize: 15, fontWeight: '800', letterSpacing: 2, color: c.slate, marginTop: 6, marginBottom: 2, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  carousel: { gap: 12, paddingVertical: 4, paddingRight: 4, alignItems: 'flex-start' },
  carouselCard: {
    width: 156,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 12,
    gap: 9,
    ...neu.raised,
  },
  carouselName: { fontSize: 14, fontWeight: '800', letterSpacing: 0.5, color: c.slate, textAlign: 'center' },
  medalWrap: { height: 132, alignItems: 'center', justifyContent: 'center' },
  cardImg: { width: '100%', height: 132, borderRadius: radii.tile, overflow: 'hidden', backgroundColor: t.navyRaised },
  cardImgPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.navy },
  cardMono: { fontSize: 48, fontWeight: '800', color: t.ivoryFaint },
  cardHint: { fontSize: 10, color: c.textFaint, textAlign: 'center', fontStyle: 'italic' },
  tierArrowRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 },
  tierArrow: { fontSize: 13, fontWeight: '800', color: c.primary },
  tierArrowLabel: { flex: 1, fontSize: 9.5, fontWeight: '800', letterSpacing: 0.6, color: c.inkSoft, textAlign: 'center' },
  stageRow: { alignItems: 'center', gap: 3 },
  stageTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  stageLabel: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.primary },
  stagePts: { fontSize: 9.5, fontWeight: '700', color: c.textFaint, letterSpacing: 0.2 },
  stageDots: { flexDirection: 'row', gap: 5 },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: t.track, ...neu.inset },
  dotFilled: { backgroundColor: c.primary },
  maladyBack: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    ...neu.inset,
  },
  miniSect: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.textFaint, marginTop: 6, marginBottom: 3 },
  miniBody: { fontSize: 11, lineHeight: 15, color: c.slate },
  miniSym: { fontSize: 11, lineHeight: 16, color: c.slate },
  swapsWrap: { gap: 10 },
  swapsEmpty: {
    fontSize: 12,
    color: c.textFaint,
    fontStyle: 'italic',
    lineHeight: 17,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    padding: 12,
    ...neu.inset,
  },
  swapRow: { borderRadius: radii.panel, backgroundColor: t.glass, padding: 14, gap: 10, ...neu.raised },
  swapRowHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  swapRemove: { fontSize: 14, fontWeight: '800', color: c.textFaint },
  swapTier: { fontSize: 11, fontWeight: '800', letterSpacing: 1.5, color: c.textFaint, marginBottom: 8 },
  swapFrontRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  swapEndCol: { width: 66, alignItems: 'center', gap: 6 },
  swapEndImg: {
    width: 56,
    height: 56,
    borderRadius: 16,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.navyRaised,
    ...neu.raisedSm,
  },
  swapEndImgInner: { width: '100%', height: '100%' },
  swapEndMono: { fontSize: 22, fontWeight: '800' },
  swapEndName: { fontSize: 12.5, fontWeight: '800', letterSpacing: 0.3, textAlign: 'center' },
  swapStatus: { marginTop: 8, textAlign: 'right', fontSize: 10.5, letterSpacing: 1.2, fontWeight: '800', color: c.textFaint },
  swapAddActions: { flexDirection: 'row', gap: 8, justifyContent: 'flex-end' },
  addSwapBtn: {
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 9,
    ...neu.raisedSm,
  },
  addSwapBtnPrimary: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  addSwapText: { fontSize: 12, fontWeight: '800', letterSpacing: 0.5, color: c.slate },
  addSwapTextPrimary: { color: t.onGold },
  modalBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 28 },
  modalSheet: {
    width: '100%',
    maxWidth: 420,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 16,
    ...neu.raised,
  },
  modalTitle: { fontSize: 13, fontWeight: '800', letterSpacing: 1, color: c.slate, marginBottom: 10, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  modalItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    marginBottom: 6,
    ...neu.raisedSm,
  },
  modalItemText: { fontSize: 13, color: c.slate },
  focusBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 20 },
  focusCard: { width: '100%', maxWidth: 380, maxHeight: '90%' },
  focusInner: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 14,
    ...neu.raised,
  },
  focusHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  focusHint: { fontSize: 10.5, letterSpacing: 0.5, fontStyle: 'italic', color: c.textFaint },
  focusClose: { fontSize: 16, fontWeight: '800', color: c.textFaint },
  focusScroll: { maxHeight: 600 },
  stepList: { gap: 8, marginTop: 2 },
  stepOpt: {
    backgroundColor: t.glass,
    borderRadius: 13,
    paddingVertical: 11,
    paddingHorizontal: 13,
    borderWidth: 2,
    borderColor: 'transparent',
    ...neu.raisedSm,
  },
  stepOptSel: { borderColor: c.primary },
  stepRadio: { width: 18, height: 18, borderRadius: 9, backgroundColor: t.track, ...neu.inset },
  stepRadioSel: { backgroundColor: c.primary },
  stepPickRow: { flexDirection: 'row', alignItems: 'center', gap: 11 },
  stepCheck: {
    width: 20,
    height: 20,
    borderRadius: 6,
    backgroundColor: t.track,
    alignItems: 'center',
    justifyContent: 'center',
    ...neu.inset,
  },
  stepCheckOn: { backgroundColor: c.primary },
  stepCheckMark: { color: t.onGold, fontSize: 13, fontWeight: '900', lineHeight: 15 },
  stepOptText: { flex: 1, gap: 1 },
  pickLock: { fontSize: 10.5, fontWeight: '700', color: c.textFaint, marginTop: -4, marginBottom: 2 },
  // Challenges
  stepOptTitle: { fontSize: 13.5, fontWeight: '800', color: c.slate, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  stepOptDetail: { fontSize: 11, color: c.inkSoft, lineHeight: 14 },
  stepOptFreq: { fontSize: 11.5, fontWeight: '800', letterSpacing: 0.3, color: c.textFaint },
  stepOptFreqSel: { color: c.primary },
  doneBtn: {
    marginTop: 9,
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 14,
    paddingVertical: 6,
    ...neu.raisedSm,
  },
  doneBtnOn: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  doneBtnText: { fontSize: 11.5, fontWeight: '800', letterSpacing: 0.3, color: c.primary },
  doneBtnTextOn: { color: t.onGold },
  // Acquisition readout on the deity back
  acqBar: { gap: 6, marginTop: 2 },
  acqBarTop: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  acqPts: { fontSize: 14, fontWeight: '900', color: c.slate, letterSpacing: 0.3 },
  acqDate: { fontSize: 11, fontWeight: '700', color: c.primary },
  acqTrack: { height: 8, borderRadius: 4, backgroundColor: t.track, overflow: 'hidden', ...neu.inset },
  acqFill: { height: '100%', borderRadius: 4, backgroundColor: c.primary },
  acqBreak: { fontSize: 10.5, color: c.textFaint, letterSpacing: 0.2 },
}),
);
